/**
 * React Query Configuration
 *
 * Central configuration for @tanstack/react-query with optimized settings
 * for the Edouard.ai Crypto application.
 *
 * Features:
 * - Smart caching with configurable stale/cache times
 * - Automatic retry on network failures
 * - Background refetching on window focus and reconnect
 * - Error handling and logging
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Default Query Client Configuration
 *
 * Queries:
 * - staleTime: 5 minutes - Data considered fresh for 5 min
 * - cacheTime: 10 minutes - Unused data kept in cache for 10 min
 * - retry: 3 - Retry failed queries up to 3 times
 * - refetchOnWindowFocus: false - Disabled to avoid hammering APIs on tab switch
 *   (CoinGecko free tier rate-limits aggressively; users can manually refresh)
 * - refetchOnReconnect: true - Refetch when network reconnects (useful, low cost)
 *
 * Mutations:
 * - retry: 1 - Only retry mutations once to avoid duplicate operations
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data freshness (5 minutes)
      staleTime: 5 * 60 * 1000,

      // Cache retention (10 minutes)
      gcTime: 10 * 60 * 1000, // Previously called 'cacheTime'

      // Retry on failure (3 attempts with exponential backoff)
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Background refetch — conservative defaults to protect API rate limits
      // and battery. Per-query overrides can opt-in to focus refetch where
      // freshness matters more than API cost.
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: true,

      // Don't refetch interval by default (can be overridden per query)
      refetchInterval: false,

      // Error handling
      throwOnError: false,
    },
    mutations: {
      // Only retry mutations once to prevent duplicate operations
      retry: 1,
      retryDelay: 1000,

      // Error handling
      throwOnError: false,

      // Callbacks
      onError: (error) => {
        console.error('Mutation error:', error);
      },
    },
  },
});

/**
 * Query Keys Factory
 *
 * Centralized query key management for consistent caching
 * and invalidation across the application.
 */
export const queryKeys = {
  // Portfolio queries
  portfolio: {
    all: ['portfolio'] as const,
    detail: () => [...queryKeys.portfolio.all, 'detail'] as const,
    history: (days?: number) => [...queryKeys.portfolio.all, 'history', days] as const,
    allocation: () => [...queryKeys.portfolio.all, 'allocation'] as const,
  },

  // Trading strategies queries
  strategies: {
    all: ['strategies'] as const,
    lists: () => [...queryKeys.strategies.all, 'list'] as const,
    list: (filters?: string) => [...queryKeys.strategies.lists(), filters] as const,
    details: () => [...queryKeys.strategies.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.strategies.details(), id] as const,
  },

  // Positions queries
  positions: {
    all: ['positions'] as const,
    lists: () => [...queryKeys.positions.all, 'list'] as const,
    list: (strategyId?: string) => [...queryKeys.positions.lists(), strategyId] as const,
    details: () => [...queryKeys.positions.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.positions.details(), id] as const,
  },

  // Performance queries
  performance: {
    all: ['performance'] as const,
    strategy: (strategyId: string) => [...queryKeys.performance.all, 'strategy', strategyId] as const,
    history: (strategyId: string) => [...queryKeys.performance.all, 'history', strategyId] as const,
  },

  // Backtest queries
  backtests: {
    all: ['backtests'] as const,
    lists: () => [...queryKeys.backtests.all, 'list'] as const,
    list: (filters?: string) => [...queryKeys.backtests.lists(), filters] as const,
    details: () => [...queryKeys.backtests.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.backtests.details(), id] as const,
  },

  // Exchange connection queries
  exchange: {
    all: ['exchange'] as const,
    status: () => [...queryKeys.exchange.all, 'status'] as const,
    balance: () => [...queryKeys.exchange.all, 'balance'] as const,
  },

  // Health check
  health: {
    all: ['health'] as const,
    backend: () => [...queryKeys.health.all, 'backend'] as const,
  },
};

/**
 * Stale time configurations for different data types
 */
export const staleTimeConfig = {
  // Real-time data (30 seconds)
  realtime: 30 * 1000,

  // Frequent updates (2 minutes)
  frequent: 2 * 60 * 1000,

  // Standard (5 minutes)
  standard: 5 * 60 * 1000,

  // Slow changing (15 minutes)
  slow: 15 * 60 * 1000,

  // Static data (1 hour)
  static: 60 * 60 * 1000,

  // Infinite (never refetch automatically)
  infinite: Infinity,
};
